import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Check, X } from "lucide-react";
import { AdminShell } from "@/components/admin/AdminShell";
import { getAdminToken } from "@/lib/admin/session";
import {
  listSpecPresets,
  createSpecLabel,
  updateSpecLabel,
  deleteSpecLabel,
  createSpecValue,
  updateSpecValue,
  deleteSpecValue,
  listProducts,
} from "@/lib/admin/cms.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const Route = createFileRoute("/admin/caratteristiche/")({
  head: () => ({ meta: [{ title: "Caratteristiche · Admin" }, { name: "robots", content: "noindex" }] }),
  component: AdminCharacteristics,
});

type SpecLabel = { id: string; label: string; unit: string | null };
type SpecValue = { id: string; label_id: string; value: string };
type ProductLite = { characteristics: { label: string; value: string }[] };

function AdminCharacteristics() {
  const fetchPresets = useServerFn(listSpecPresets);
  const fetchProducts = useServerFn(listProducts);
  const createLabel = useServerFn(createSpecLabel);
  const updateLabel = useServerFn(updateSpecLabel);
  const removeLabel = useServerFn(deleteSpecLabel);
  const createValue = useServerFn(createSpecValue);
  const updateValue = useServerFn(updateSpecValue);
  const removeValue = useServerFn(deleteSpecValue);

  const [labels, setLabels] = useState<SpecLabel[]>([]);
  const [values, setValues] = useState<SpecValue[]>([]);
  const [products, setProducts] = useState<ProductLite[]>([]);
  const [loading, setLoading] = useState(true);

  const [newLabel, setNewLabel] = useState("");
  const [newUnit, setNewUnit] = useState("");
  const [editingLabelId, setEditingLabelId] = useState<string | null>(null);
  const [editLabelText, setEditLabelText] = useState("");
  const [editLabelUnit, setEditLabelUnit] = useState("");

  const [newValueByLabel, setNewValueByLabel] = useState<Record<string, string>>({});
  const [editingValueId, setEditingValueId] = useState<string | null>(null);
  const [editValueText, setEditValueText] = useState("");

  async function refresh() {
    const token = getAdminToken();
    if (!token) return;
    setLoading(true);
    try {
      const [presets, prods] = await Promise.all([
        fetchPresets({ data: { token } }),
        fetchProducts({ data: { token } }),
      ]);
      setLabels(presets.labels as SpecLabel[]);
      setValues(presets.values as SpecValue[]);
      if (presets.error) toast.warning(presets.error);
      setProducts(prods.products as unknown as ProductLite[]);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Errore");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const usageByLabel = useMemo(() => {
    const map = new Map<string, number>();
    products.forEach((p) =>
      (p.characteristics ?? []).forEach((c) => {
        const k = (c.label ?? "").toLowerCase();
        map.set(k, (map.get(k) ?? 0) + 1);
      }),
    );
    return map;
  }, [products]);

  const valuesByLabel = useMemo(() => {
    const map = new Map<string, SpecValue[]>();
    values.forEach((v) => {
      const arr = map.get(v.label_id) ?? [];
      arr.push(v);
      map.set(v.label_id, arr);
    });
    return map;
  }, [values]);

  async function handleCreateLabel() {
    const token = getAdminToken();
    if (!token || !newLabel.trim()) return;
    try {
      await createLabel({ data: { token, label: newLabel.trim(), unit: newUnit.trim() || null } });
      setNewLabel("");
      setNewUnit("");
      refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Errore");
    }
  }

  async function handleSaveLabel(id: string) {
    const token = getAdminToken();
    if (!token) return;
    try {
      await updateLabel({
        data: { token, id, label: editLabelText.trim(), unit: editLabelUnit.trim() || null },
      });
      setEditingLabelId(null);
      refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Errore");
    }
  }

  async function handleDeleteLabel(l: SpecLabel) {
    const used = usageByLabel.get(l.label.toLowerCase()) ?? 0;
    const msg = used
      ? `«${l.label}» è usata da ${used} prodotto/i. Eliminarla comunque? (i prodotti manterranno la voce, ma non sarà più nei preset)`
      : `Eliminare «${l.label}»?`;
    if (!confirm(msg)) return;
    const token = getAdminToken();
    if (!token) return;
    try {
      await removeLabel({ data: { token, id: l.id } });
      refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Errore");
    }
  }

  async function handleAddValue(labelId: string) {
    const token = getAdminToken();
    const v = (newValueByLabel[labelId] ?? "").trim();
    if (!token || !v) return;
    try {
      await createValue({ data: { token, label_id: labelId, value: v } });
      setNewValueByLabel({ ...newValueByLabel, [labelId]: "" });
      refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Errore");
    }
  }

  async function handleSaveValue(id: string) {
    const token = getAdminToken();
    if (!token || !editValueText.trim()) return;
    try {
      await updateValue({ data: { token, id, value: editValueText.trim() } });
      setEditingValueId(null);
      refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Errore");
    }
  }

  async function handleDeleteValue(v: SpecValue) {
    if (!confirm(`Eliminare «${v.value}»?`)) return;
    const token = getAdminToken();
    if (!token) return;
    try {
      await removeValue({ data: { token, id: v.id } });
      refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Errore");
    }
  }

  return (
    <AdminShell>
      <div className="space-y-6">
        <div>
          <h1 className="font-display text-3xl text-slate-900">Caratteristiche</h1>
          <p className="text-sm text-slate-600">
            Gestisci le etichette (es. <em>Frollatura</em>, <em>Provenienza</em>) e i loro valori preset.
            L&apos;unità di misura, se impostata, fa apparire un campo numerico nel form prodotto.
          </p>
        </div>

        {/* Crea nuova etichetta */}
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_180px_auto] gap-3 items-end">
            <div>
              <Label>Nuova etichetta</Label>
              <Input
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder="es. Frollatura"
              />
            </div>
            <div>
              <Label>Unità (opzionale)</Label>
              <Input
                value={newUnit}
                onChange={(e) => setNewUnit(e.target.value)}
                placeholder="es. giorni, kg, °C"
              />
            </div>
            <Button onClick={handleCreateLabel} className="bg-brand-red hover:bg-brand-red-hover">
              <Plus className="h-4 w-4 mr-1" /> Aggiungi
            </Button>
          </div>
        </div>

        {/* Elenco etichette */}
        <div className="space-y-3">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Caricamento...</div>
          ) : labels.length === 0 ? (
            <div className="p-8 text-center text-slate-500 bg-white rounded-lg border border-slate-200">
              Nessuna etichetta. Creane una sopra.
            </div>
          ) : (
            labels.map((l) => {
              const usage = usageByLabel.get(l.label.toLowerCase()) ?? 0;
              const vals = valuesByLabel.get(l.id) ?? [];
              const isEditing = editingLabelId === l.id;
              return (
                <div key={l.id} className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                  <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-slate-100 bg-slate-50">
                    {isEditing ? (
                      <>
                        <Input
                          value={editLabelText}
                          onChange={(e) => setEditLabelText(e.target.value)}
                          className="max-w-xs"
                        />
                        <Input
                          value={editLabelUnit}
                          onChange={(e) => setEditLabelUnit(e.target.value)}
                          placeholder="unità"
                          className="max-w-[140px]"
                        />
                        <Button size="sm" onClick={() => handleSaveLabel(l.id)} className="bg-brand-red hover:bg-brand-red-hover">
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingLabelId(null)}>
                          <X className="h-4 w-4" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <span className="font-medium text-slate-900">{l.label}</span>
                        {l.unit && (
                          <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-800 rounded">
                            unità: {l.unit}
                          </span>
                        )}
                        <span className="text-xs text-slate-500">
                          {vals.length} valori · usata in {usage} prodotti
                        </span>
                        <div className="ml-auto flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setEditingLabelId(l.id);
                              setEditLabelText(l.label);
                              setEditLabelUnit(l.unit ?? "");
                            }}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDeleteLabel(l)}>
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="p-4 space-y-2">
                    {vals.length === 0 && (
                      <div className="text-xs text-slate-400">Nessun valore preset.</div>
                    )}
                    {vals.map((v) => (
                      <div key={v.id} className="flex items-center gap-2">
                        {editingValueId === v.id ? (
                          <>
                            <Input
                              value={editValueText}
                              onChange={(e) => setEditValueText(e.target.value)}
                              className="max-w-md"
                            />
                            <Button size="sm" onClick={() => handleSaveValue(v.id)} className="bg-brand-red hover:bg-brand-red-hover">
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditingValueId(null)}>
                              <X className="h-4 w-4" />
                            </Button>
                          </>
                        ) : (
                          <>
                            <span className="px-3 py-1 rounded-md bg-brand-cream border border-brand-gold/20 text-sm">
                              {v.value}
                              {l.unit && <span className="text-slate-500 ml-1">{l.unit}</span>}
                            </span>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setEditingValueId(v.id);
                                setEditValueText(v.value);
                              }}
                            >
                              <Pencil className="h-3 w-3" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleDeleteValue(v)}>
                              <Trash2 className="h-3 w-3 text-red-600" />
                            </Button>
                          </>
                        )}
                      </div>
                    ))}
                    <div className="flex gap-2 pt-2 border-t border-slate-100">
                      <Input
                        value={newValueByLabel[l.id] ?? ""}
                        onChange={(e) => setNewValueByLabel({ ...newValueByLabel, [l.id]: e.target.value })}
                        placeholder={l.unit ? `Valore (es. 21)` : "Nuovo valore preset"}
                        className="max-w-md"
                        onKeyDown={(e) => e.key === "Enter" && handleAddValue(l.id)}
                      />
                      <Button size="sm" variant="outline" onClick={() => handleAddValue(l.id)}>
                        <Plus className="h-4 w-4 mr-1" /> Aggiungi valore
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </AdminShell>
  );
}